import React, { useEffect, useMemo, memo } from "react";
import { Table, Placeholder } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { analyticsActions } from "../../actions/analytics.actions";
import { getDateRange } from "../../utils/dateUtils";

const RunbookOverview = memo(function RunbookOverview(props) {
  const dispatch = useDispatch();
  const analyticsReducer = useSelector((state) => state.analytics);

  useEffect(() => {
    if (!props.user) return;

    // Use clientId prop if provided (for super user viewing specific client),
    // otherwise use user's clientId
    const clientId = props.clientId || props.user.clientId;
    if (!clientId) return;

    const queryParams = {
      clientId: clientId,
      clientGuid: props.user.clientGuid,
    };

    const { startDate, endDate } = getDateRange(props.dateRange);
    if (startDate) queryParams.startDate = startDate;
    if (endDate) queryParams.endDate = endDate;

    dispatch(analyticsActions.getRunbookOverview(queryParams));
    // eslint-disable-next-line
  }, [props.user, props.dateRange, props.clientId]);

  const runbooks = analyticsReducer.runbookOverview || [];
  const loading = analyticsReducer.loadingRunbookOverview;

  // Memoize formatted runbook data
  const formattedRunbooks = useMemo(() => {
    return runbooks.map((runbook) => ({
      ...runbook,
      formattedSuccessRate: runbook.successRate.toFixed(2),
      formattedRecords: runbook.totalRecordsReturned.toLocaleString(),
      formattedLastExecution: runbook.lastExecutionTime
        ? new Date(runbook.lastExecutionTime).toLocaleString()
        : "N/A",
    }));
  }, [runbooks]);

  return (
    <div>
      <h5 className="mb-3">Runbook Execution Overview</h5>
      <p className="text-muted">Click on a runbook to view detailed execution history</p>

      {loading && (
        <Placeholder as={"div"} animation="glow">
          <Placeholder xs={12} size="lg" style={{ height: "300px" }} />
        </Placeholder>
      )}

      {!loading && formattedRunbooks.length > 0 && (
        <Table striped bordered hover responsive>
          <thead>
            <tr>
              <th>Runbook ID</th>
              <th>Runbook Name</th>
              <th>Total Executions</th>
              <th>Successful</th>
              <th>Failed</th>
              <th>Success Rate</th>
              <th>Records Returned</th>
              <th>Last Execution</th>
            </tr>
          </thead>
          <tbody>
            {formattedRunbooks.map((runbook) => (
              <tr
                key={runbook.runbookId}
                onClick={() => props.onRunbookClick(runbook)}
                style={{ cursor: "pointer" }}
                className="hover-row"
              >
                <td>{runbook.runbookId}</td>
                <td><strong>{runbook.runbookName}</strong></td>
                <td>{runbook.totalExecutions}</td>
                <td>{runbook.successfulExecutions}</td>
                <td>{runbook.failedExecutions}</td>
                <td>{runbook.formattedSuccessRate}%</td>
                <td>{runbook.formattedRecords}</td>
                <td>{runbook.formattedLastExecution}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}

      {!loading && formattedRunbooks.length === 0 && (
        <div className="text-center text-muted py-5">
          <p>No runbook execution data available for the selected date range</p>
        </div>
      )}
    </div>
  );
});

export default RunbookOverview;
